import { withContexts } from "./wrappers/withContexts";
import { ReactNode, CSSProperties, createContext, useMemo } from "react";
import { Box, useTheme, useMediaQuery } from "@mui/material";

export type ContentProps = {
    style?: CSSProperties;
};

export type PdfHeaderConfig = {
    enabled?: boolean;
    logoUrl?: string;
    logoHeight?: number;
    title?: string;
    subtitle?: string;
    alignment?: "left" | "center" | "right";
};

export type PdfFooterConfig = {
    enabled?: boolean;
    showPageNumbers?: boolean;
    pageNumberFormat?: "page" | "pageOfTotal";
    text?: string;
    alignment?: "left" | "center" | "right" | "spread";
};

export type RootProps = {
    className?: string;
    children?: ReactNode;
    pageType?: string;
    export?: string;
    description?: string;
    downloadOnlyOnSsqValidated?: boolean;
    score?: string | string[];
    company?: string | string[];
    entity?: string | string[];
    pdfOptions?: unknown;
    pdfSettings?: {
        format?: "A4" | "A3" | "Letter" | "Legal";
        landscape?: "portrait" | "landscape";
        marginsMm?: {
            top?: number;
            right?: number;
            bottom?: number;
            left?: number;
        };
    };
    pdfHeader?: PdfHeaderConfig;
    pdfFooter?: PdfFooterConfig;
};

export const RootPropsContext = createContext<RootProps | null>(null);

const RootComponent = (props: RootProps) => {
    const {
        children,
        pdfSettings: _pdfSettings,
        pageType: _pageType,
        downloadOnlyOnSsqValidated: _downloadOnlyOnSsqValidated,
        score: _score,
        company: _company,
        entity: _entity,
        pdfHeader: _pdfHeader,
        pdfFooter: _pdfFooter,
        pdfOptions: _pdfOptions,
        ...rest
    } = props;
    delete (rest as Record<string, unknown>).title;
    const theme = useTheme();

    const rootPropsForContext = useMemo<RootProps>(
        () => ({
            pageType: _pageType,
            pdfSettings: _pdfSettings,
            pdfHeader: _pdfHeader,
            pdfFooter: _pdfFooter,
            downloadOnlyOnSsqValidated: _downloadOnlyOnSsqValidated,
            score: _score,
            company: _company,
            entity: _entity,
        }),
        [_company, _downloadOnlyOnSsqValidated, _entity, _pageType, _pdfFooter, _pdfHeader, _pdfSettings, _score],
    );
    const isPdfMode = useMemo(() => {
        if (_pageType === "PDF") return true;
        if (typeof window === "undefined") return false;
        return new URLSearchParams(window.location.search).get("__pdf") === "1";
    }, [_pageType]);

    const isPdfExportMode = useMemo(() => {
        if (typeof window === "undefined") return false;
        return new URLSearchParams(window.location.search).get("__pdf") === "1";
    }, []);

    const pdfPageWidthMm = useMemo(() => {
        const landscape = _pdfSettings?.landscape === "landscape";

        const [w, h] = [297, 420];
        return landscape ? h : w;
    }, [_pdfSettings?.landscape]);

    const pdfPrintableWidthMm = useMemo(() => {
        const left = _pdfSettings?.marginsMm?.left ?? 0;
        const right = _pdfSettings?.marginsMm?.right ?? 0;

        const safeEpsilon = 0.5;
        const width = pdfPageWidthMm - left - right - safeEpsilon;
        return Math.max(50, width);
    }, [pdfPageWidthMm, _pdfSettings?.marginsMm?.left, _pdfSettings?.marginsMm?.right]);

    const pdfEditorPadding = useMemo(() => {
        const top = _pdfSettings?.marginsMm?.top ?? 0;
        const right = _pdfSettings?.marginsMm?.right ?? 0;
        const bottom = _pdfSettings?.marginsMm?.bottom ?? 0;
        const left = _pdfSettings?.marginsMm?.left ?? 0;
        return `${top}mm ${right}mm ${bottom}mm ${left}mm`;
    }, [
        _pdfSettings?.marginsMm?.bottom,
        _pdfSettings?.marginsMm?.left,
        _pdfSettings?.marginsMm?.right,
        _pdfSettings?.marginsMm?.top,
    ]);

    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    return (
        <RootPropsContext.Provider value={rootPropsForContext}>
            <Box
                {...rest}
                sx={{
                    minHeight: "100vh",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                    boxSizing: "border-box",
                    maxWidth:
                        isPdfMode ?
                            isPdfExportMode ? `${pdfPrintableWidthMm}mm`
                            :   `${pdfPageWidthMm}mm`
                        :   undefined,
                    margin: isPdfMode ? "0 auto" : undefined,
                    padding:
                        isPdfMode ?
                            isPdfExportMode ? 0
                            :   pdfEditorPadding
                        : isMobile ? theme.spacing(0, 2)
                        : 0,
                    "& div[class*='_DropZone--isRootZone_']": {
                        minHeight: "100vh",
                    },
                }}
            >
                {children}
            </Box>
        </RootPropsContext.Provider>
    );
};

export const RootBare = RootComponent;

const Root = withContexts(RootComponent);
export default Root;
